import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import type {
  CardRowSection,
  EditorialEntry,
  EditorialListSection,
  FeatureAccent,
  FeatureGridSection,
  LandingSection,
  ProductSpotlightSection,
  SplitMediaSection,
} from './landingSectionsCms';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';
import { departmentPath } from '../../utils/departmentRouting';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function storefrontMediaSrc(raw: string | undefined): string {
  const url = String(raw ?? '').trim();
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${API_BASE.replace(/\/$/, '')}${url}`;
  return url;
}

const ACCENT_DISK: Record<FeatureAccent, string> = {
  teal: 'bg-teal-500 shadow-lg shadow-teal-500/30',
  purple: 'bg-violet-600 shadow-lg shadow-violet-600/30',
  amber: 'bg-amber-400 shadow-lg shadow-amber-400/35',
  rose: 'bg-rose-500 shadow-lg shadow-rose-500/30',
  emerald: 'bg-emerald-500 shadow-lg shadow-emerald-500/30',
  orange: 'bg-orange-500 shadow-lg shadow-orange-500/30',
};

function splitTitleAccent(title: string) {
  const parts = title.trim().split(/\s+/);
  const first = parts[0] ?? title;
  const rest = parts.slice(1).join(' ');
  return { first, rest };
}

function CtaControl({
  href,
  label,
  className,
}: {
  href?: string;
  label: string;
  className?: string;
}) {
  const navigate = useNavigate();
  if (!href || !label) return null;
  const internal = href.startsWith('/') && !href.startsWith('//');
  if (internal) {
    return (
      <button type="button" onClick={() => navigate(href)} className={className}>
        {label}
      </button>
    );
  }
  return (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

type SpotlightProduct = {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  salePrice?: number | null;
  pricePerUnit?: number;
  category?: {
    name?: string | null;
    department?: { name?: string | null; slug?: string | null } | null;
  } | null;
};

function shuffleStable<T>(items: T[], seedStr: string): T[] {
  let seed = 0;
  for (let i = 0; i < seedStr.length; i++) {
    seed = (seed * 31 + seedStr.charCodeAt(i)) >>> 0;
  }
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const j = seed % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Prefer one product per department when mixing across departments; otherwise fill to five. */
function pickSpotlightProducts(products: SpotlightProduct[]): {
  featured: SpotlightProduct;
  side: SpotlightProduct[];
} | null {
  const usable = products.filter((p) => String(p.id || '').trim() && String(p.name || '').trim());
  if (!usable.length) return null;

  const byDept = new Map<string, SpotlightProduct[]>();
  for (const p of usable) {
    const slug = String(p.category?.department?.slug || '__').trim() || '__';
    if (!byDept.has(slug)) byDept.set(slug, []);
    byDept.get(slug)!.push(p);
  }

  const deptKeys = shuffleStable([...byDept.keys()], usable.map((p) => p.id).join('|'));
  const picked: SpotlightProduct[] = [];
  let round = 0;
  while (picked.length < 5) {
    let added = false;
    for (const dk of deptKeys) {
      const bucket = byDept.get(dk)!;
      if (picked.length >= 5) break;
      if (bucket.length > round) {
        const p = bucket[round];
        if (p && !picked.some((x) => x.id === p.id)) {
          picked.push(p);
          added = true;
        }
      }
    }
    if (!added) break;
    round++;
  }
  for (const p of usable) {
    if (picked.length >= 5) break;
    if (!picked.some((x) => x.id === p.id)) picked.push(p);
  }
  if (!picked.length) return null;
  return { featured: picked[0]!, side: picked.slice(1, 5) };
}

function clipText(raw: string | null | undefined, max: number): string {
  const s = String(raw ?? '').trim().replace(/\s+/g, ' ');
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

function productSpotlightCopy(p: SpotlightProduct): string {
  const desc = clipText(p.description ?? '', 220);
  if (desc) return desc;
  const cat = String(p.category?.name ?? '').trim();
  if (cat) return cat;
  const dept = String(p.category?.department?.name ?? '').trim();
  return dept || '';
}

function spotlightProductPath(
  storefrontDepartmentSlug: string | null,
  p: SpotlightProduct,
): string {
  const pDept = p.category?.department?.slug?.trim();
  const scope = storefrontDepartmentSlug ?? pDept ?? null;
  return departmentPath(scope, `/products/${p.id}`);
}

function EditorialLink({
  entry,
  className,
  children,
}: {
  entry: EditorialEntry;
  className?: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const href = entry.href?.trim();
  if (!href) {
    return <div className={className}>{children}</div>;
  }
  const internal = href.startsWith('/') && !href.startsWith('//');
  if (internal) {
    return (
      <button type="button" onClick={() => navigate(href)} className={className}>
        {children}
      </button>
    );
  }
  return (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function SplitMediaBlock({ s }: { s: SplitMediaSection }) {
  const imageLeft = s.imagePosition === 'left';
  const bg =
    s.background === 'warm'
      ? 'bg-gradient-to-br from-amber-50/95 via-stone-50 to-orange-50/80'
      : s.background === 'muted'
        ? 'bg-slate-100/90'
        : 'bg-gradient-to-b from-slate-50 to-white';

  const copy = (
    <div>
      {s.kicker ? (
        <p
          className="text-[11px] font-bold uppercase tracking-[0.22em]"
          style={{ color: 'var(--storefront-brand-primary, #059669)' }}
        >
          {s.kicker}
        </p>
      ) : null}
      <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{s.title}</h2>
      <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-600">{s.body}</p>
      <div className="mt-8">
        <CtaControl
          href={s.ctaHref}
          label={s.ctaLabel ?? ''}
          className="inline-flex rounded-full bg-gradient-to-r from-[#ff6b35] to-[#f7931e] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/25 transition hover:brightness-105"
        />
      </div>
    </div>
  );

  const media = (
    <div className="relative">
      <div
        className="absolute -inset-4 rounded-tl-[2.75rem] rounded-br-[2.75rem] rounded-bl-2xl rounded-tr-2xl bg-gradient-to-br from-emerald-700/12 via-transparent to-orange-200/22 blur-2xl"
        aria-hidden
      />
      <div className="relative overflow-hidden rounded-tl-[2.5rem] rounded-br-[2.5rem] rounded-tr-xl rounded-bl-xl border border-slate-200 bg-slate-200">
        <img
          src={storefrontMediaSrc(s.imageUrl)}
          alt={s.imageAlt || s.title}
          className="aspect-[4/3] h-full w-full object-cover sm:aspect-[5/4]"
          loading="lazy"
        />
      </div>
    </div>
  );

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={`relative overflow-hidden ${bg}`}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/35 to-transparent"
        aria-hidden
      />
      <div className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
          {imageLeft ? (
            <>
              {media}
              {copy}
            </>
          ) : (
            <>
              {copy}
              {media}
            </>
          )}
        </div>
      </div>
    </motion.section>
  );
}

function FeatureGridBlock({ s }: { s: FeatureGridSection }) {
  const cols = s.columns === 2 ? 'sm:grid-cols-2' : s.columns === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3';
  const { first, rest } = splitTitleAccent(s.title);
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        {s.eyebrow ? (
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">{s.eyebrow}</p>
        ) : null}
        <h2 className="mt-2 text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff6b35] to-[#f7931e]">{first}</span>
          {rest ? <span className="text-slate-900"> {rest}</span> : null}
        </h2>
        {s.subtitle ? (
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-slate-600 sm:text-base">{s.subtitle}</p>
        ) : null}
        <div className={`mt-10 grid gap-5 ${cols}`}>
          {s.items.map((it, i) => (
            <motion.article
              key={`${s.id}-${i}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 transition-colors hover:border-slate-300"
            >
              <div className="flex flex-col gap-4">
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full text-lg text-white ${ACCENT_DISK[it.accent]}`}
                >
                  <i className={it.icon.startsWith('fa-') ? `fa ${it.icon}` : `fa fa-${it.icon}`} aria-hidden />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{it.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.body}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

function CardRowBlock({ s }: { s: CardRowSection }) {
  const { i18n, t } = useTranslation();
  const [packages, setPackages] = React.useState<
    Array<{
      id: string;
      name: string;
      slug: string;
      headline?: string | null;
      heroImageUrl?: string | null;
      priceUsd?: number | string;
      items?: Array<{
        qty: number;
        unit: 'LB' | 'KG' | 'PACK';
        product?: {
          id?: string;
          name?: string | null;
          salePrice?: number | null;
          pricePerUnit?: number | null;
          isActive?: boolean;
          currentStockLbs?: number | null;
        } | null;
      }>;
      isActive?: boolean;
    }>
  >([]);
  const [loadingPackages, setLoadingPackages] = React.useState(true);
  const navigate = useNavigate();
  const { addLine } = useCart();
  const { formatPrice } = useCurrency();

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingPackages(true);
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/catalog/packages`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) {
          if (!cancelled) setPackages([]);
          return;
        }
        const data = (await res.json()) as Array<{
          id: string;
          name: string;
          slug: string;
          headline?: string | null;
          heroImageUrl?: string | null;
          priceUsd?: number | string;
          items?: Array<{
            qty: number;
            unit: 'LB' | 'KG' | 'PACK';
            product?: {
              id?: string;
              name?: string | null;
              salePrice?: number | null;
              pricePerUnit?: number | null;
              isActive?: boolean;
              currentStockLbs?: number | null;
            } | null;
          }>;
          isActive?: boolean;
        }>;
        if (!cancelled) {
          setPackages(Array.isArray(data) ? data.filter((p) => p.isActive !== false).slice(0, 3) : []);
        }
      } catch {
        if (!cancelled) setPackages([]);
      } finally {
        if (!cancelled) setLoadingPackages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  const { first, rest } = splitTitleAccent(
    t('storefront.landing_package_bundles_title', {
      defaultValue: 'Curated package bundles',
    }),
  );
  if (!loadingPackages && packages.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-gradient-to-b from-slate-100/95 to-slate-50/90"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
          {t('storefront.landing_package_offers', {
            defaultValue: 'Package offers',
          })}
        </p>
        {s.title ? (
          <h2 className="mt-2 text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff6b35] to-[#f7931e]">{first}</span>
            {rest ? <span className="text-slate-900"> {rest}</span> : null}
          </h2>
        ) : null}
        <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-600 sm:text-base">
          {t('storefront.landing_package_bundles_subtitle', {
            defaultValue:
              'Explore ready-made bundles with transparent pricing and included items.',
          })}
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {packages.map((pkg, i: number) => (
            (() => {
              const toLbs = (qty: number, unit: 'LB' | 'KG' | 'PACK') =>
                unit === 'KG' ? qty * 2.20462 : qty;
              const needByProduct = new Map<string, number>();
              const stockByProduct = new Map<string, number>();
              let packageAvailable = true;
              for (const it of pkg.items || []) {
                const pid = it.product?.id;
                if (!pid || it.product?.isActive === false) {
                  packageAvailable = false;
                  break;
                }
                const need = toLbs(Number(it.qty || 0), it.unit);
                if (!Number.isFinite(need) || need <= 0) {
                  packageAvailable = false;
                  break;
                }
                needByProduct.set(pid, (needByProduct.get(pid) ?? 0) + need);
                stockByProduct.set(pid, Number(it.product?.currentStockLbs ?? 0));
              }
              if (!needByProduct.size) packageAvailable = false;
              for (const [pid, needed] of needByProduct) {
                if ((stockByProduct.get(pid) ?? 0) + 1e-9 < needed) {
                  packageAvailable = false;
                  break;
                }
              }
              return (
            <motion.article
              key={pkg.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={storefrontMediaSrc(pkg.heroImageUrl || '')}
                  alt={pkg.headline || pkg.name}
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="text-xl font-extrabold tracking-tight text-white drop-shadow-sm">
                    {pkg.headline || pkg.name}
                  </p>
                  <div className="mt-1">
                    <p className="text-sm font-extrabold text-rose-200 line-through decoration-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                      {formatPrice(
                        (pkg.items || []).reduce((sum, it) => {
                          const unitPrice = Number(it.product?.salePrice ?? it.product?.pricePerUnit ?? 0);
                          const qty = Number(it.qty ?? 0);
                          if (!Number.isFinite(unitPrice) || unitPrice <= 0) return sum;
                          if (!Number.isFinite(qty) || qty <= 0) return sum;
                          return sum + unitPrice * qty;
                        }, 0),
                      )}
                    </p>
                    <p className="text-3xl font-extrabold leading-none text-emerald-300 drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
                      {formatPrice(Number(pkg.priceUsd ?? 0))}
                    </p>
                  </div>
                  {pkg.items?.length ? (
                    <ul className="mt-2 line-clamp-3 text-sm font-medium text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.75)]">
                      {pkg.items.slice(0, 3).map((it, idx) => (
                        <li key={`${pkg.id}-it-${idx}`}>
                          {it.qty} {it.unit} {it.product?.name || 'item'}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={!packageAvailable}
                      onClick={() => addLine({ kind: 'package', packageId: pkg.id, qty: 1 })}
                      className="inline-flex rounded-full bg-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-md hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-500"
                    >
                      {t('storefront.add_to_cart', { defaultValue: 'Add to cart' })}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/packages/${pkg.slug}`)}
                      className="inline-flex rounded-full bg-gradient-to-r from-[#ff6b35] to-[#f7931e] px-4 py-2 text-xs font-bold text-white shadow-md"
                    >
                      {t('common.see_more', { defaultValue: 'See more' })}
                    </button>
                  </div>
                  {!packageAvailable ? (
                    <p className="mt-2 text-xs font-semibold text-amber-200">
                      {t('storefront.unavailable_package', {
                        defaultValue: 'Unavailable package',
                      })}
                    </p>
                  ) : null}
                </div>
              </div>
            </motion.article>
              );
            })()
          ))}
        </div>
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate('/packages')}
            className="inline-flex rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-800 transition hover:border-slate-400"
          >
            {t('storefront.show_more_packages', {
              defaultValue: 'Show more packages',
            })}
          </button>
        </div>
      </div>
    </motion.section>
  );
}

function ProductSpotlightBlock({
  eyebrow,
  title,
  subtitle,
  storefrontDepartmentSlug,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  storefrontDepartmentSlug: string | null;
}) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [products, setProducts] = React.useState<SpotlightProduct[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const url = new URL(`${API_BASE}/catalog/products`);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', storefrontDepartmentSlug ? '12' : '48');
        url.searchParams.set('activeOnly', 'true');
        url.searchParams.set('allowOutOfStock', 'true');
        if (storefrontDepartmentSlug) {
          url.searchParams.set('departmentSlug', storefrontDepartmentSlug);
        }
        const res = await fetch(
          applyContentLocaleToUrl(url.toString(), i18n.resolvedLanguage || i18n.language),
        );
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { items?: SpotlightProduct[] };
        if (!cancelled) setProducts(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storefrontDepartmentSlug, i18n.language, i18n.resolvedLanguage]);

  const picked = React.useMemo(() => pickSpotlightProducts(products), [products]);
  const { first, rest } = splitTitleAccent(title);

  if (loading) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.08 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="bg-gradient-to-b from-white via-slate-50/80 to-white"
      >
        <div className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
          <div className="h-7 w-56 max-w-full animate-pulse rounded-lg bg-slate-200" />
          <div className="mt-10 grid gap-8 lg:grid-cols-5 lg:gap-10">
            <div className="lg:col-span-3">
              <div className="aspect-[16/10] animate-pulse rounded-xl bg-slate-200" />
              <div className="mt-6 space-y-2">
                <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                <div className="h-6 w-full max-w-md animate-pulse rounded bg-slate-200" />
              </div>
            </div>
            <div className="flex flex-col gap-4 lg:col-span-2">
              {[0, 1, 2, 3].map((k) => (
                <div key={k} className="h-24 animate-pulse rounded-lg bg-slate-200" />
              ))}
            </div>
          </div>
        </div>
      </motion.section>
    );
  }

  if (!picked) return null;

  const { featured, side } = picked;
  const featuredCopy = productSpotlightCopy(featured);

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-gradient-to-b from-white via-slate-50/80 to-white"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        {eyebrow ? (
          <p
            className="text-[11px] font-bold uppercase tracking-[0.22em]"
            style={{ color: 'var(--storefront-brand-primary, #059669)' }}
          >
            {eyebrow}
          </p>
        ) : null}
        <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff6b35] to-[#f7931e]">{first}</span>
          {rest ? <span className="text-slate-900"> {rest}</span> : null}
        </h2>
        {subtitle ? <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base">{subtitle}</p> : null}

        <div className="mt-10 grid gap-8 lg:grid-cols-5 lg:gap-10">
          <div className="lg:col-span-3">
            <button
              type="button"
              onClick={() => navigate(spotlightProductPath(storefrontDepartmentSlug, featured))}
              className="block w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-left transition-colors hover:border-emerald-200/80"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-200">
                {featured.imageUrl ? (
                  <img
                    src={storefrontMediaSrc(featured.imageUrl)}
                    alt={featured.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full min-h-[200px] items-center justify-center text-slate-400">
                    <i className="fa fa-image text-4xl" aria-hidden />
                  </div>
                )}
              </div>
              <div className="p-6 sm:p-8">
                <p className="flex flex-wrap items-center gap-2 text-xs font-semibold text-emerald-700">
                  <span>{formatPrice(Number(featured.salePrice ?? featured.pricePerUnit ?? 0))}</span>
                  {featured.category?.department?.name ? (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                      {featured.category.department.name}
                    </span>
                  ) : null}
                </p>
                <h3 className="mt-2 text-xl font-bold text-slate-900 sm:text-2xl">{featured.name}</h3>
                {featuredCopy ? (
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{featuredCopy}</p>
                ) : null}
              </div>
            </button>
          </div>
          <div className="flex flex-col gap-4 lg:col-span-2">
            {side.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => navigate(spotlightProductPath(storefrontDepartmentSlug, e))}
                className="flex gap-4 overflow-hidden rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:border-emerald-200/70"
              >
                <div className="relative h-24 w-28 shrink-0 overflow-hidden rounded-xl bg-slate-200">
                  {e.imageUrl ? (
                    <img src={storefrontMediaSrc(e.imageUrl)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-400">
                      <i className="fa fa-image" aria-hidden />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1 py-1">
                  <p className="flex flex-wrap items-center gap-2 text-[11px] font-semibold text-emerald-700">
                    <span>{formatPrice(Number(e.salePrice ?? e.pricePerUnit ?? 0))}</span>
                    {e.category?.department?.name ? (
                      <span className="truncate rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-600">
                        {e.category.department.name}
                      </span>
                    ) : null}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-900">{e.name}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function EditorialListBlock({ s }: { s: EditorialListSection }) {
  const { first, rest } = splitTitleAccent(s.title);
  const f = s.featured;
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-gradient-to-b from-white via-slate-50/80 to-white"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
        {s.eyebrow ? (
          <p
            className="text-[11px] font-bold uppercase tracking-[0.22em]"
            style={{ color: 'var(--storefront-brand-primary, #059669)' }}
          >
            {s.eyebrow}
          </p>
        ) : null}
        <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff6b35] to-[#f7931e]">{first}</span>
          {rest ? <span className="text-slate-900"> {rest}</span> : null}
        </h2>
        {s.subtitle ? <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base">{s.subtitle}</p> : null}

        <div className="mt-10 grid gap-8 lg:grid-cols-5 lg:gap-10">
          <div className="lg:col-span-3">
            <EditorialLink
              entry={f}
              className="block w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-left transition-colors hover:border-emerald-200/80"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-200">
                <img src={storefrontMediaSrc(f.imageUrl)} alt={f.title} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="p-6 sm:p-8">
                <p className="flex items-center gap-2 text-xs font-semibold text-rose-600">
                  <i className="fa fa-calendar-days text-rose-500" aria-hidden />
                  {f.date}
                </p>
                <h3 className="mt-2 text-xl font-bold text-slate-900 sm:text-2xl">{f.title}</h3>
                {f.body ? <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.body}</p> : null}
              </div>
            </EditorialLink>
          </div>
          <div className="flex flex-col gap-4 lg:col-span-2">
            {s.entries.map((e, i) => (
              <EditorialLink
                key={e.id || `${s.id}-e-${i}`}
                entry={e}
                className="flex gap-4 overflow-hidden rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:border-emerald-200/70"
              >
                {e.imageUrl ? (
                  <div className="relative h-24 w-28 shrink-0 overflow-hidden rounded-xl bg-slate-200">
                    <img src={storefrontMediaSrc(e.imageUrl)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div className="h-24 w-28 shrink-0 rounded-xl bg-slate-100" />
                )}
                <div className="min-w-0 flex-1 py-1">
                  <p className="flex items-center gap-2 text-[11px] font-semibold text-rose-600">
                    <i className="fa fa-calendar-days text-rose-500" aria-hidden />
                    {e.date}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-900">{e.title}</p>
                </div>
              </EditorialLink>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

export default function ConfigurableLandingSections({
  sections,
  departmentSlug = null,
}: {
  sections: LandingSection[];
  /** When set (department-scoped storefront), spotlight uses only this department’s catalog. */
  departmentSlug?: string | null;
}) {
  const visible = React.useMemo(
    () =>
      [...sections]
        .filter((s) => s.enabled !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [sections],
  );

  if (!visible.length) return null;

  return (
    <div className="flex flex-col">
      {visible.map((s) => {
        if (s.type === 'SPLIT_MEDIA') return <SplitMediaBlock key={s.id} s={s} />;
        if (s.type === 'FEATURE_GRID') return <FeatureGridBlock key={s.id} s={s} />;
        if (s.type === 'CARD_ROW') return <CardRowBlock key={s.id} s={s} />;
        if (s.type === 'PRODUCT_SPOTLIGHT') {
          const ps = s as ProductSpotlightSection;
          return (
            <ProductSpotlightBlock
              key={ps.id}
              eyebrow={ps.eyebrow}
              title={ps.title}
              subtitle={ps.subtitle}
              storefrontDepartmentSlug={departmentSlug}
            />
          );
        }
        if (s.type === 'EDITORIAL_LIST' && s.id === 'kitchen-journal') {
          return (
            <ProductSpotlightBlock
              key={s.id}
              eyebrow={s.eyebrow}
              title={s.title}
              subtitle={s.subtitle}
              storefrontDepartmentSlug={departmentSlug}
            />
          );
        }
        if (s.type === 'EDITORIAL_LIST') return <EditorialListBlock key={s.id} s={s} />;
        return null;
      })}
    </div>
  );
}
